import React, { useState, useEffect, useLayoutEffect } from 'react';
import uniqid from 'uniqid';

import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import PageNav from 'containers/user/learn/components/page-nav/page-nav';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';

import api from 'api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import { onResize } from 'helpers/resize';
import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

interface ReleaseNoteData {
  name?: string;
  releaseNotesDate?: string;
  content?: Array<{ id: string | number; type: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface ProductAnnouncementProps {
  releaseNoteId: string | number;
}

const ProductAnnouncement = ({
  releaseNoteId,
}: ProductAnnouncementProps): React.JSX.Element => {
  const [releaseNote, setReleaseNote] = useState<ReleaseNoteData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isTabletView, setIsTabletView] = useState<boolean>(false);

  const user = useCurrentUser();

  useEffect(() => {
    setLoading(true);
    api
      .getReleaseNote(releaseNoteId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setReleaseNote(
          (res as { data: { data: { attributes: ReleaseNoteData } } }).data.data.attributes,
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useLayoutEffect(() => {
    onResize(setIsTabletView);
  }, []);

  return loading ? (
    <Loader {...{ loading }} />
  ) : (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          {
            label: 'Product Announcements',
            href: '/learn/content/product-announcements',
          },
        ]}
        customClass="!mt-5"
      />
      <Heading variant="h1" customClass="text-4xl font-semibold mobile:pt-4 xs:pt-6 mb-2">
        {releaseNote.name}
      </Heading>

      <div className="flex items-center text-navy-500 mb-4">
        {releaseNote.releaseNotesDate && (
          <>
            <Icons.Calendar className="h-4 w-4 mr-1" />
            <div className="text-sm">
              {formatDate(releaseNote.releaseNotesDate, 'MMM D, YYYY')}
            </div>
          </>
        )}
      </div>

      {isTabletView && (
        <PageNav content={releaseNote.content} isTabletView={true} />
      )}
      <div className="flex mobile:flex-col md:flex-row order-1 justify-between">
        <ResponsiveWrapper customClass="flex flex-col">
          {releaseNote.content?.map((component) =>
            getBlocksFromContent({ ...component, key: uniqid() })
          )}
        </ResponsiveWrapper>
        <div className="flex flex-col">
          {!isTabletView && <PageNav content={releaseNote.content} />}
        </div>
      </div>
    </ResponsiveWrapper>
  );
};

export default ProductAnnouncement;
